import { useState } from 'react'
import { ISetting, ISettings } from '../../model/ISettings'
import toast from 'react-hot-toast'
import { settingsApi } from '../services/settings'
import { getErrorFromCatch, serializeFieldErrors } from '../services/api'

export type ISettingsState = ISettings

export default function useSettings(defaultState: ISettingsState) {
  const [state, setState] = useState<ISettingsState>(() => defaultState)
  const [loading, setLoading] = useState<boolean>(false)

  const handleUpdate = (id: ISetting['idSetting'], value?: string): void => {
    // -- Get the setting group
    const settingGroup = Object.entries(state).find(([group, settings]) =>
      settings.find((s) => s.idSetting === id),
    )
    if (!settingGroup) return
    const [groupName, settings] = settingGroup

    // -- Get the setting index
    const settingIndx = settings.findIndex((s) => s.idSetting === id)

    // -- Get the original value and calculate the new value
    const originalSetting = settings[settingIndx]
    value ??= `${+originalSetting?.value ? 0 : 1}`

    // -- Update the setting
    const tmpSettings = [...settings]
    tmpSettings[settingIndx] = {
      ...settings[settingIndx],
      value: (originalSetting.type === 'boolean' && value) || value || '',
    }

    // -- Update lenked settings if exist (for boolean settings)
    const radio_group = tmpSettings[settingIndx].radio_group
    radio_group &&
      settings.forEach((s, indx) => {
        if (s.radio_group === radio_group && indx !== settingIndx)
          s.type === 'boolean' && (tmpSettings[indx] = { ...s, value: `${+value! ? 0 : 1}` })
      })

    // -- Update the state
    setState((prevState) => ({
      ...prevState,
      [groupName]: tmpSettings,
    }))
  }

  const handleReset = () => setState(() => defaultState)

  const [updateSettings] = settingsApi.useUpdateSettingsMutation()
  const handleSubmit = async (): Promise<Record<string, string> | null> => {
    try {
      setLoading(() => true)
      await updateSettings(state).unwrap()
      toast.success(`Settings have been updated successfully`, { duration: 5000 })
      return null
    } catch (err) {
      toast.error(`${getErrorFromCatch(err)}`, { duration: 5000 })
      return serializeFieldErrors(err) ?? { error: getErrorFromCatch(err) }
    } finally {
      setLoading(() => false)
    }
  }

  return {
    state,
    loading,
    handleUpdate,
    handleSubmit,
    handleReset,
  }
}
